import type { ContentfulLinkType, ContentfulNavigationType } from '@bedrock/core/contentful';

export function getItemHref(item: ContentfulLinkType & { anchor?: string }): string {
  const base = item.linkedPage ? `/${item.linkedPage.slug}` : item.url || '';
  if (base.startsWith('http://') || base.startsWith('https://')) return '';
  const anchor = item.anchor?.replace('#', '');
  return anchor ? `${base}#${anchor}` : base;
}

function normalizePath(path: string): string {
  return path === '/home' ? '/' : path;
}

export function isItemActive(
  item: ContentfulNavigationType | ContentfulLinkType,
  pathname: string,
  hash: string,
  excludedPaths?: Set<string>,
): boolean {
  if (isNavigationType(item)) {
    return item.navigation.some((child) => isItemActive(child, pathname, hash, excludedPaths));
  }
  const href = getItemHref(item);
  if (!href) return false;
  if (excludedPaths?.has(href)) return false;
  const normalizedPathname = normalizePath(pathname);
  const currentFull = hash ? `${normalizedPathname}#${hash}` : normalizedPathname;
  const hashIndex = href.indexOf('#');
  const basePath = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const fragment = hashIndex >= 0 ? href.slice(hashIndex + 1) : '';
  const normalizedBase = normalizePath(basePath);
  const normalizedHref = fragment ? `${normalizedBase}#${fragment}` : normalizedBase;
  if (normalizedHref.includes('#')) return currentFull === normalizedHref;
  return normalizedPathname === normalizedHref;
}

export function collectDirectLinkPaths(
  items: (ContentfulNavigationType | ContentfulLinkType | null)[],
): Set<string> {
  const paths = new Set<string>();
  for (const item of items) {
    if (item && !isNavigationType(item)) {
      const href = getItemHref(item);
      if (href) paths.add(href);
    }
  }
  return paths;
}

export function isNavigationType(
  navigationItem?: ContentfulNavigationType | ContentfulLinkType,
): navigationItem is ContentfulNavigationType {
  return navigationItem?.sysType === 'navigation';
}

export function getMenuTriggerTitle(
  navItem?: ContentfulNavigationType | ContentfulLinkType,
): string | undefined {
  if (!navItem) return;

  if (!isNavigationType(navItem)) {
    return navItem.label;
  }

  return (
    navItem.title ?? (navItem.linkedPage ? getMenuTriggerTitle(navItem.linkedPage) : navItem.title)
  );
}

export function flatNavigationLinks(
  navigation: (ContentfulNavigationType | ContentfulLinkType)[],
): (ContentfulNavigationType | ContentfulLinkType)[] {
  return navigation.map((item) => {
    if (!isNavigationType(item)) {
      return item;
    }

    return {
      ...item,
      navigation: [...item.navigation],
    };
  });
}

export function getLinkToParentPage(node?: ContentfulNavigationType): ContentfulLinkType | null {
  if (!node?.linkedPage) return null;

  const linkedPage = node.linkedPage;

  const targetLink = isNavigationType(linkedPage) ? linkedPage.linkedPage : linkedPage;

  if (!targetLink || isNavigationType(targetLink) || !targetLink.linkedPage) return null;

  return targetLink;
}
